// Le journal de la journée (§9.2) — fil horodaté alimenté automatiquement.
// Utilité concrète : quand la comptabilité appelle trois jours après pour
// demander pourquoi tel écart, la réponse est là en dix secondes.
//
// Il est lisible depuis SotCP V3 (onglet en lecture seule dans le détail d'une
// journée) — c'est pourquoi fc_etapes stocke le libellé, et fc_journal le nom
// de l'acteur : la V3 n'a pas à connaître les constantes de FlashCP.
//
// Les filtres sont ENTIÈREMENT LOCAUX : le journal est chargé une fois, filtrer
// ne rappelle jamais le serveur. « Dix secondes » se joue là.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { formatDate } from 'sotcp-core/dayUtils'
import * as api from '@/data/api'
import { useRun } from '@/data/RunProvider'
import { heureSec } from '@/lib/format'
import { messageErreur } from '@/lib/errors'
import { aller } from '@/lib/router'
import { Card, Chip, Lamp, Notice, Vide } from '@/ui/primitives'
import { useToast } from '@/ui/Toast'
import type { Couleur } from '@/ui/primitives'
import type { EvenementJournal } from '@/domain/types'

const LIBELLES: Record<string, [string, Couleur]> = {
  run_ouverte:          ['Run ouverte', 'act'],
  entree_run:           ['Entrée dans la run', 'act'],
  sortie_run:           ['Sortie de la run', 'neutre'],
  prise_de_main:        ['Prise de main sur le verrou', 'warn'],
  etape_commencee:      ['Étape commencée', 'act'],
  etape_validee:        ['Étape validée', 'ok'],
  etape_validee_forcee: ['Étape validée EN FORÇANT', 'warn'],
  etape_de_cote:        ['Étape mise de côté', 'warn'],
  etape_rouverte:       ['Étape rouverte', 'warn'],
  etape_grisee_regle:   ['Étape grisée par règle', 'neutre'],
  etape_degrisee_regle: ['Étape dégrisée', 'neutre'],
  reponse_question:     ['Réponse à la question d’entrée', 'neutre'],
  branche_validee:      ['Branche validée', 'ok'],
  branche_skip_question:['Branche déclarée non applicable', 'neutre'],
  branche_en_cours:     ['Branche ouverte', 'act'],
  pad_en_attente:       ['PAD entré en attente', 'warn'],
  pad_resolu:           ['PAD résolu', 'ok'],
  pad_bascule_ecart:    ['PAD basculé en écart', 'warn'],
  run_cloturee:         ['Journée clôturée', 'ok'],
  note:                 ['Note', 'neutre'],
}

const libelleType = (t: string): string => LIBELLES[t]?.[0] ?? t

export default function Journal() {
  const { date, noter } = useRun()
  const toast = useToast()
  const [lignes, setLignes] = useState<EvenementJournal[]>([])
  // La journée à laquelle `lignes` appartient. Sans elle, changer de journée
  // laisse à l'écran le fil de la PRÉCÉDENTE sous le titre de la nouvelle —
  // et le compteur d'événements avec : on lit les faits du mauvais jour, ce
  // qui est exactement ce que ce journal existe pour empêcher.
  const [chargeePour, setChargeePour] = useState<string | null>(null)
  const [chargement, setChargement] = useState(true)
  const [erreur, setErreur] = useState<string | null>(null)
  const [filtre, setFiltre] = useState('')
  const [recherche, setRecherche] = useState('')
  const [texte, setTexte] = useState('')
  const [ecrit, setEcrit] = useState(false)

  // Un chargement en vol doit pouvoir être abandonné : sans ce drapeau, changer
  // de journée deux fois de suite laisse la réponse la plus lente écraser la
  // plus récente, et un démontage écrit dans un composant qui n'existe plus.
  const seq = useRef(0)
  const charger = useCallback(() => {
    // La demande la plus récente gagne, d'où qu'elle vienne — changement de
    // journée, « Réessayer », relecture après une note. Avec un simple drapeau
    // local, une relecture lancée après un ajout pouvait répondre APRÈS celle
    // du jour suivant et réinstaller la mauvaise journée : l'écran restait
    // alors bloqué sur « Lecture du journal… ».
    const n = ++seq.current
    const actuelle = () => n === seq.current
    setChargement(true)
    api.chargerJournal(date)
      .then((l) => { if (actuelle()) { setLignes(l); setChargeePour(date); setErreur(null) } })
      .catch((e) => { if (actuelle()) setErreur(messageErreur(e)) })
      .finally(() => { if (actuelle()) setChargement(false) })
    return () => { if (actuelle()) seq.current++ }   // démontage : on abandonne
  }, [date])

  useEffect(charger, [charger])

  // Rien de ce qui vient d'une autre journée ne s'affiche, même en attendant.
  const aJour = chargeePour === date
  const evenements = useMemo(() => (aJour ? lignes : []), [aJour, lignes])

  const types = useMemo(
    () => Array.from(new Set(evenements.map((l) => l.type))).sort((a, b) =>
      libelleType(a).localeCompare(libelleType(b), 'fr')),
    [evenements],
  )

  // Le filtrage se fait sur le texte déjà rendu à l'écran : on cherche ce qu'on
  // voit — un libellé, un acteur, un motif, un numéro d'étape.
  const visibles = useMemo(() => {
    const q = recherche.trim().toLowerCase()
    return evenements.filter((l) => {
      if (filtre && l.type !== filtre) return false
      if (!q) return true
      const foin = [
        libelleType(l.type), l.type, l.etape ?? '', l.acteur_nom ?? '', l.note ?? '', detail(l),
      ].join(' ').toLowerCase()
      return foin.includes(q)
    })
  }, [evenements, filtre, recherche])

  const filtreActif = filtre !== '' || recherche.trim() !== ''
  const effacerFiltres = () => { setFiltre(''); setRecherche('') }

  const ajouter = async () => {
    const t = texte.trim()
    if (!t || ecrit) return              // Entrée maintenue = deux notes identiques
    setEcrit(true)
    try {
      await noter(t)
      setTexte('')
      charger()
      toast('NOTE AJOUTÉE AU JOURNAL')
    } catch (e) { toast(messageErreur(e), true) }
    finally { setEcrit(false) }
  }

  const exporter = () => {
    if (visibles.length === 0) return
    // Un point-virgule ou un retour à la ligne dans une cellule casse le fichier :
    // on les neutralise partout, pas seulement dans la note.
    const cel = (v: unknown): string => String(v ?? '').replace(/[;\r\n]+/g, ' ').trim()
    const csv = ['at;type;etape;acteur;note;payload']
      .concat(visibles.map((l) => [
        cel(l.at), cel(l.type), cel(l.etape), cel(l.acteur_nom), cel(l.note),
        // payload peut être null en base : JSON.stringify(undefined) rend
        // `undefined` (pas une chaîne) et l'export explosait sur .replace().
        cel(JSON.stringify(l.payload ?? {})),
      ].join(';')))
      .join('\r\n')
    // Le BOM : sans lui, Excel en fr-FR affiche « ÉTAPE » en « Ã‰TAPE ».
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `flashcp-journal-${date}.csv`
    // Firefox ignore un clic sur un lien hors du document.
    document.body.appendChild(a)
    a.click()
    a.remove()
    // Révoquer dans la foulée coupe parfois le téléchargement avant qu'il parte.
    window.setTimeout(() => URL.revokeObjectURL(url), 30_000)
  }

  return (
    <div className="wrap" style={{ padding: '18px 0 60px' }}>
      <div className="st-head">
        <h1 className="st-title">
          <small>
            JOURNAL DE LA JOURNÉE ·{' '}
            {!aJour ? '…'
              : filtreActif ? `${visibles.length} SUR ${evenements.length}`
              : `${evenements.length}`} ÉVÉNEMENTS
          </small>
          {formatDate(date)}
        </h1>
        <div className="tools">
          <input className="fld" style={{ width: 'auto', minWidth: 190 }} type="search"
                 aria-label="Rechercher dans le journal"
                 placeholder="Rechercher : acteur, motif, étape…"
                 value={recherche} onChange={(e) => setRecherche(e.target.value)}
                 onKeyDown={(e) => { if (e.key === 'Escape') setRecherche('') }} />
          <select className="fld" style={{ width: 'auto', minWidth: 190 }}
                  aria-label="Filtrer par type d’événement"
                  value={filtre} onChange={(e) => setFiltre(e.target.value)}>
            <option value="">Tous les types</option>
            {types.map((t) => <option key={t} value={t}>{libelleType(t)}</option>)}
          </select>
          {filtreActif && (
            <button className="btn gh sm" onClick={effacerFiltres}>Effacer les filtres</button>
          )}
          {/* L'export n'est pas le geste du jour : ton discret. */}
          <button className="btn gh sm" disabled={visibles.length === 0} onClick={exporter}>
            Exporter en CSV{filtreActif ? ` (${visibles.length})` : ''}
          </button>
          <button className="btn" onClick={() => aller({ vue: 'run', date, etape: null })}>← La run</button>
        </div>
      </div>

      {erreur && (
        <Notice c="stop" action={
          <button className="btn sm" data-encours={chargement ? 'true' : undefined}
                  onClick={() => { charger() }}>Réessayer</button>
        }>{erreur}</Notice>
      )}

      <Card titre="Note libre" style={{ marginBottom: 14 }}>
        <div style={{ display: 'flex', gap: 8, padding: 12 }}>
          <input className="fld" placeholder="Ce qui n’est écrit nulle part ailleurs…"
                 value={texte} onChange={(e) => setTexte(e.target.value)}
                 onKeyDown={(e) => {
                   if (e.key === 'Enter') void ajouter()
                   if (e.key === 'Escape') setTexte('')
                 }} />
          <button className="btn pri" data-encours={ecrit ? 'true' : undefined}
                  disabled={texte.trim() === ''} onClick={() => void ajouter()}>
            Ajouter la note
          </button>
        </div>
        <div className="hint" style={{ padding: '0 12px 10px' }}>
          Entrée pour ajouter · Échap pour effacer le champ.
        </div>
      </Card>

      {/* Une relecture sur une journée déjà affichée ne change rien à l'écran :
          sans ce témoin, « Réessayer » et l'ajout d'une note semblent sans effet. */}
      <Card titre="Fil horodaté"
            actions={chargement && aJour ? <Chip c="act">LECTURE…</Chip> : undefined}>
        {!aJour
          ? <Vide>{erreur ? 'Journal illisible — voir l’erreur ci-dessus.' : 'Lecture du journal…'}</Vide>
          : visibles.length === 0
            ? filtreActif
              ? <Vide>
                  Aucun événement ne correspond au filtre.
                  <div style={{ marginTop: 10 }}>
                    <button className="btn sm" onClick={effacerFiltres}>Effacer les filtres</button>
                  </div>
                </Vide>
              : <Vide>Le journal de cette journée est vide — rien n’a encore été fait.</Vide>
            : visibles.map((l) => {
              const [libelle, couleur] = LIBELLES[l.type] ?? [l.type, 'neutre' as const]
              return (
                <div className="li" key={l.id}>
                  <Lamp c={couleur} />
                  <div className="body">
                    <div className="t">
                      {libelle}{l.etape ? ` — étape ${l.etape}` : ''}
                      {l.note && <span style={{ fontWeight: 400 }}> : {l.note}</span>}
                    </div>
                    <div className="s">
                      {heureSec(l.at)} · {(l.acteur_nom ?? '—').toUpperCase()}
                      {detail(l) && ` · ${detail(l)}`}
                    </div>
                  </div>
                  {l.type === 'etape_validee_forcee' && <Chip c="warn">FORCÉE</Chip>}
                </div>
              )
            })}
      </Card>
    </div>
  )
}

function detail(l: EvenementJournal): string {
  const p = l.payload ?? {}
  const bouts: string[] = []
  if (typeof p.motif === 'string') bouts.push(p.motif.toUpperCase())
  if (typeof p.precedent === 'string') bouts.push(`REPRISE À ${p.precedent.toUpperCase()}`)
  if (typeof p.branche === 'string') bouts.push(`BRANCHE ${p.branche.toUpperCase()}`)
  if (typeof p.pad_ref === 'string') bouts.push(p.pad_ref)
  if (typeof p.oui === 'boolean') bouts.push(p.oui ? 'OUI' : 'NON')
  if (typeof p.validee_par === 'string') bouts.push(`AVAIT ÉTÉ VALIDÉE PAR ${p.validee_par.toUpperCase()}`)
  return bouts.join(' · ')
}
